import { SourcingContext } from '@luminovo/api-contracts';
import { compareByDate, isPresent } from '@luminovo/commons';
import { httpQuery, useHttpQuery, useSuspenseHttpQuery } from '@luminovo/data-stack';
import type { QueryClient } from '@tanstack/react-query';

export function useQuoteRequests() {
    return useSuspenseHttpQuery(
        'POST /quote-request/find',
        {
            requestBody: {},
        },
        {
            select: (data) => Array.from(data.items).sort(compareByDate((n) => n.created_at)),
        },
    );
}

export function useQuoteRequest(quoteRequestId: string | undefined) {
    return useHttpQuery(
        'POST /quote-request/find',
        {
            requestBody: {
                quote_request_ids: isPresent(quoteRequestId) ? [quoteRequestId] : [],
            },
        },
        {
            enabled: isPresent(quoteRequestId),
            select: (data) => data.items[0],
        },
    );
}

/**
 * Unlike {@link useQuoteRequest}, fetches all quote requests and selects the one with the given `quoteRequestId`.
 * There is an implicity performance tradeoff here, the assumption being that you will often want to jump between quote requests.
 */
export function useSuspendedQuoteRequest(quoteRequestId: string) {
    return useSuspenseHttpQuery(
        'GET /quote-request/:id',
        {
            pathParams: {
                id: quoteRequestId,
            },
        },
        {
            select: (data) => data,
        },
    );
}

export function useQuoteRequestsBulk(quoteRequestIds: string[] | undefined) {
    return useHttpQuery(
        'POST /quote-request/find',
        {
            requestBody: {
                quote_request_ids: quoteRequestIds ?? [],
            },
        },
        {
            select: (data) => Array.from(data.items).sort(compareByDate((n) => n.created_at)),
            enabled: isPresent(quoteRequestIds),
        },
    );
}

export function useSuspenseQuoteRequestsBulk(quoteRequestIds: string[]) {
    return useSuspenseHttpQuery(
        'POST /quote-request/find',
        {
            requestBody: {
                quote_request_ids: quoteRequestIds,
            },
        },
        {
            select: (data) => Array.from(data.items).sort(compareByDate((n) => n.created_at)),
        },
    );
}

/**
 * Whether the API returns every one of `quoteRequestIds` and each belongs to `project`, for the loader of a page
 * that shows them under that negotiation or project. It asks with the query {@link useQuoteRequestsBulk} and
 * {@link useSuspenseQuoteRequestsBulk} read them with, so the page asks no second time, and asks nothing for no ids.
 */
export async function quoteRequestsBelongTo(
    queryClient: QueryClient,
    quoteRequestIds: string[],
    project: SourcingContext,
): Promise<boolean> {
    if (quoteRequestIds.length === 0) {
        return true;
    }
    const { items } = await queryClient.ensureQueryData(
        httpQuery('POST /quote-request/find', { requestBody: { quote_request_ids: quoteRequestIds } }),
    );
    // Every requested id must come back: one the API leaves out was deleted or doesn't exist.
    const found = new Map(items.map((quoteRequest) => [quoteRequest.id, quoteRequest]));
    return quoteRequestIds.every((id) => {
        const owner = found.get(id)?.project;
        return owner?.type === project.type && owner.id === project.id;
    });
}

export function useQuoteRequestByRfq(rfqId: string) {
    return useSuspenseHttpQuery(
        'POST /quote-request/find',
        {
            requestBody: { rfq_id: rfqId },
        },
        {
            select: (data) => Array.from(data.items).sort(compareByDate((n) => n.created_at)),
        },
    );
}

export function useQuoteRequestByNegotiation(negotiationId: string) {
    return useSuspenseHttpQuery(
        'POST /quote-request/find',
        {
            requestBody: { negotiation_v2_id: negotiationId },
        },
        {
            select: (data) => Array.from(data.items).sort(compareByDate((n) => n.created_at)),
        },
    );
}

/** For code shared by project sourcing and Negotiations. */
export function useQuoteRequestsBySourcingContext(sourcingContext: SourcingContext) {
    return useSuspenseHttpQuery(
        'POST /quote-request/find',
        {
            requestBody:
                sourcingContext.type === 'Rfq'
                    ? { rfq_id: sourcingContext.id }
                    : { negotiation_v2_id: sourcingContext.id },
        },
        {
            select: (data) => Array.from(data.items).sort(compareByDate((n) => n.created_at)),
        },
    );
}

export function useQuoteRequestLineItemsByQuoteRequest(quoteRequestId: string) {
    return useSuspenseHttpQuery(
        'GET /quote-request/:id/line-items',
        {
            pathParams: { id: quoteRequestId },
        },
        {
            select: (data) => data.items,
        },
    );
}

export function useQuoteRequestLineItemsByQuoteRequests(quoteRequestIds: string[] | undefined) {
    return useHttpQuery(
        'POST /quote-request/line-items',
        {
            requestBody: {
                quote_request_ids: quoteRequestIds ?? [],
            },
        },
        {
            select: (data) => data.items,
            enabled: isPresent(quoteRequestIds),
        },
    );
}
